import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Layers, Grid3X3, Paintbrush, Cable, Mountain, Calculator as CalcIcon } from "lucide-react";
import { inr, waLink } from "@/lib/config";
import { downloadEstimatePdf } from "@/lib/estimatePdf";

const pdfTitle = (m) =>
  m.includes("requirement") ? "Aggregate & Sand Estimate" : `${m.split(" estimate")[0]} Estimate`;

const BANNER =
  "https://images.unsplash.com/photo-1503387837-b154d5074bd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBlbmdpbmVlciUyMGJsdWVwcmludHMlMjBhcmNoaXRlY3R8ZW58MHx8fHwxNzg4MTcwMDU2fDA&ixlib=rb-4.1.0&q=85&w=1600";

const TABS = [
  { id: "concrete", label: "Concrete Mix", icon: Layers, testid: "calc-tab-concrete" },
  { id: "brickwork", label: "Brickwork", icon: Grid3X3, testid: "calc-tab-brickwork" },
  { id: "plaster", label: "Plastering", icon: Paintbrush, testid: "calc-tab-plaster" },
  { id: "steel", label: "Steel TMT", icon: Cable, testid: "calc-tab-steel" },
  { id: "aggregates", label: "Aggregates", icon: Mountain, testid: "calc-tab-aggregates" },
];

const MIX_GRADES = {
  M10: { ratio: [1, 3, 6], label: "M10 (1:3:6)" },
  M15: { ratio: [1, 2, 4], label: "M15 (1:2:4)" },
  M20: { ratio: [1, 1.5, 3], label: "M20 (1:1.5:3)" },
  M25: { ratio: [1, 1, 2], label: "M25 (1:1:2)" },
  M30: { ratio: [1, 0.75, 1.5], label: "M30 (1:0.75:1.5)" },
};

const inputCls =
  "w-full bg-ink border border-white/10 focus:border-brand/60 outline-none text-sm text-gray-200 placeholder-gray-600 px-4 py-3 transition-colors font-mono";

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">{label}</span>
      <input data-testid={testid} className={inputCls} {...props} />
    </label>
  );
}

function ResultRow({ label, value, unit, testid, highlight }) {
  return (
    <div className={`flex items-baseline justify-between py-2.5 border-b border-white/5 ${highlight ? "text-brand" : "text-gray-300"}`}>
      <span className="font-mono text-xs uppercase tracking-widest text-gray-500">{label}</span>
      <span className="font-mono text-lg font-bold" data-testid={testid}>
        {value} <span className="text-xs text-gray-500 font-normal">{unit}</span>
      </span>
    </div>
  );
}

const n = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
};

function fmt(v, d = 1) {
  if (!Number.isFinite(v)) return "0";
  return v >= 100 ? inr(v) : v.toFixed(d);
}

function ActionBar({ message }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      <a
        href={waLink(message)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="calc-btn-whatsapp-estimate"
        className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-ink font-heading font-bold text-sm uppercase tracking-wide px-5 py-3 transition-colors"
      >
        <MessageCircle className="h-4 w-4" /> Order via WhatsApp
      </a>
      <button
        onClick={() => downloadEstimatePdf(pdfTitle(message), message)}
        data-testid="calc-btn-download-pdf"
        className="inline-flex items-center gap-2 border border-white/20 hover:border-brand/60 text-gray-200 hover:text-brand font-heading font-bold text-sm uppercase tracking-wide px-5 py-3 transition-colors"
      >
        Download PDF
      </button>
      <button
        onClick={() => window.print()}
        className="inline-flex items-center gap-2 border border-white/20 hover:border-brand/60 text-gray-200 hover:text-brand font-heading font-bold text-sm uppercase tracking-wide px-5 py-3 transition-colors"
      >
        Print Breakdown
      </button>
    </div>
  );
}

function ConcreteCalc() {
  const [f, setF] = useState({ length: "10", width: "10", depth: "0.125", grade: "M20", rate: "385" });
  const r = useMemo(() => {
    const vol = n(f.length) * n(f.width) * n(f.depth);
    const dry = vol * 1.54;
    const [c, s, a] = MIX_GRADES[f.grade].ratio;
    const sum = c + s + a;
    const cementM3 = (dry * c) / sum;
    const bags = Math.ceil((cementM3 * 1440) / 50);
    const sandM3 = (dry * s) / sum;
    const aggM3 = (dry * a) / sum;
    const water = cementM3 * 1440 * 0.5;
    return {
      vol,
      bags,
      sandT: sandM3 * 1.6,
      sandFt3: sandM3 * 35.3147,
      aggT: aggM3 * 1.55,
      aggFt3: aggM3 * 35.3147,
      water,
      cost: bags * n(f.rate),
    };
  }, [f]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Length (m)" type="number" min="0" step="any" value={f.length} onChange={(e) => setF({ ...f, length: e.target.value })} testid="calc-input-length" />
          <Field label="Width (m)" type="number" min="0" step="any" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} testid="calc-input-width" />
          <Field label="Depth (m)" type="number" min="0" step="any" value={f.depth} onChange={(e) => setF({ ...f, depth: e.target.value })} testid="calc-input-depth" />
        </div>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Mix Grade</span>
          <select data-testid="calc-select-mix-grade" value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} className={inputCls}>
            {Object.entries(MIX_GRADES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <Field label="Cement rate (₹/bag)" type="number" min="0" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} testid="calc-input-cement-rate" />
        <p className="font-mono text-[11px] text-gray-600">Dry volume factor 1.54 applied · Cement density 1440 kg/m³ · W/C 0.5</p>
      </div>
      <div className="bg-ink border border-white/5 p-6">
        <ResultRow label="Wet volume" value={fmt(r.vol, 2)} unit="m³" />
        <ResultRow label="Cement bags (50kg)" value={r.bags} unit="bags" testid="calc-result-cement-bags" highlight />
        <ResultRow label="Sand" value={`${fmt(r.sandT)} / ${fmt(r.sandFt3, 0)}`} unit="t / ft³" testid="calc-result-sand-tonnes" />
        <ResultRow label="Aggregate (10+20mm)" value={`${fmt(r.aggT)} / ${fmt(r.aggFt3, 0)}`} unit="t / ft³" testid="calc-result-aggregate-tonnes" />
        <ResultRow label="Water" value={fmt(r.water, 0)} unit="litres" />
        <ResultRow label="Est. cement cost" value={`₹${inr(r.cost)}`} unit="" highlight />
        <ActionBar message={`Concrete estimate (${f.grade}): ${f.length}×${f.width}×${f.depth}m slab → ${r.bags} cement bags, sand ${fmt(r.sandT)}t, aggregate ${fmt(r.aggT)}t. Please quote landed rate.`} />
      </div>
    </div>
  );
}

function BrickworkCalc() {
  const [f, setF] = useState({ length: "10", height: "3", thickness: "0.2", ratio: "4", rate: "385" });
  const r = useMemo(() => {
    const vol = n(f.length) * n(f.height) * n(f.thickness);
    const bricks = Math.ceil(vol / 0.002);
    const mortar = vol * 0.3;
    const dry = mortar * 1.33;
    const x = parseFloat(f.ratio) || 4;
    const bags = Math.ceil(((dry / (1 + x)) * 1440) / 50);
    const sandM3 = (dry * x) / (1 + x);
    return { vol, bricks, bags, sandM3, sandFt3: sandM3 * 35.3147, cost: bags * n(f.rate) };
  }, [f]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Wall length (m)" type="number" min="0" step="any" value={f.length} onChange={(e) => setF({ ...f, length: e.target.value })} testid="calc-input-brick-length" />
          <Field label="Wall height (m)" type="number" min="0" step="any" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} testid="calc-input-brick-height" />
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Thickness</span>
            <select value={f.thickness} onChange={(e) => setF({ ...f, thickness: e.target.value })} className={inputCls} data-testid="calc-select-brick-thickness">
              <option value="0.1">4″ (0.10 m)</option>
              <option value="0.2">8″ (0.20 m)</option>
              <option value="0.23">9″ (0.23 m)</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Mortar Ratio</span>
          <select value={f.ratio} onChange={(e) => setF({ ...f, ratio: e.target.value })} className={inputCls} data-testid="calc-select-mortar-ratio">
            <option value="3">1 : 3 (rich)</option>
            <option value="4">1 : 4</option>
            <option value="6">1 : 6</option>
          </select>
        </label>
        <Field label="Cement rate (₹/bag)" type="number" min="0" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} />
        <p className="font-mono text-[11px] text-gray-600">Modular brick 0.2×0.1×0.1 m with mortar · 30% mortar volume · Dry factor 1.33</p>
      </div>
      <div className="bg-ink border border-white/5 p-6">
        <ResultRow label="Wall volume" value={fmt(r.vol, 2)} unit="m³" />
        <ResultRow label="Bricks required" value={inr(r.bricks)} unit="nos" highlight />
        <ResultRow label="Cement bags (50kg)" value={r.bags} unit="bags" highlight />
        <ResultRow label="Sand" value={`${fmt(r.sandM3)} / ${fmt(r.sandFt3, 0)}`} unit="m³ / ft³" />
        <ResultRow label="Est. cement cost" value={`₹${inr(r.cost)}`} unit="" />
        <ActionBar message={`Brickwork estimate: ${f.length}×${f.height}m wall (${f.thickness}m, 1:${f.ratio}) → ${inr(r.bricks)} bricks, ${r.bags} cement bags, sand ${fmt(r.sandM3)} m³. Please quote.`} />
      </div>
    </div>
  );
}

function PlasterCalc() {
  const [f, setF] = useState({ length: "10", height: "3", thickness: "0.012", ratio: "4", rate: "385" });
  const r = useMemo(() => {
    const area = n(f.length) * n(f.height);
    const vol = area * parseFloat(f.thickness);
    const dry = vol * 1.33;
    const x = parseFloat(f.ratio) || 4;
    const bags = Math.ceil(((dry / (1 + x)) * 1440) / 50);
    const sandM3 = (dry * x) / (1 + x);
    return { area, bags, sandM3, sandFt3: sandM3 * 35.3147, cost: bags * n(f.rate) };
  }, [f]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Length (m)" type="number" min="0" step="any" value={f.length} onChange={(e) => setF({ ...f, length: e.target.value })} testid="calc-input-plaster-length" />
          <Field label="Height (m)" type="number" min="0" step="any" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} testid="calc-input-plaster-height" />
        </div>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Plaster Thickness</span>
          <select value={f.thickness} onChange={(e) => setF({ ...f, thickness: e.target.value })} className={inputCls} data-testid="calc-select-plaster-thickness">
            <option value="0.012">12 mm (internal)</option>
            <option value="0.015">15 mm (internal rough)</option>
            <option value="0.02">20 mm (external)</option>
          </select>
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Mortar Ratio</span>
          <select value={f.ratio} onChange={(e) => setF({ ...f, ratio: e.target.value })} className={inputCls}>
            <option value="4">1 : 4 (ceiling / external)</option>
            <option value="6">1 : 6 (internal walls)</option>
          </select>
        </label>
        <Field label="Cement rate (₹/bag)" type="number" min="0" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} />
      </div>
      <div className="bg-ink border border-white/5 p-6">
        <ResultRow label="Plaster area" value={fmt(r.area, 1)} unit="m²" />
        <ResultRow label="Cement bags (50kg)" value={r.bags} unit="bags" highlight />
        <ResultRow label="Sand" value={`${fmt(r.sandM3)} / ${fmt(r.sandFt3, 0)}`} unit="m³ / ft³" />
        <ResultRow label="Est. cement cost" value={`₹${inr(r.cost)}`} unit="" />
        <ActionBar message={`Plastering estimate: ${fmt(r.area, 1)} m² @ ${parseFloat(f.thickness) * 1000}mm, 1:${f.ratio} → ${r.bags} cement bags, sand ${fmt(r.sandM3)} m³. Please quote.`} />
      </div>
    </div>
  );
}

function SteelCalc() {
  const [f, setF] = useState({ dia: "12", rods: "100", rate: "58500" });
  const r = useMemo(() => {
    const d = n(f.dia);
    const kgm = (d * d) / 162;
    const rodWt = kgm * 12;
    const totalKg = rodWt * n(f.rods);
    return { kgm, rodWt, totalKg, totalMT: totalKg / 1000, cost: (totalKg / 1000) * n(f.rate) };
  }, [f]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Bar Diameter</span>
          <select value={f.dia} onChange={(e) => setF({ ...f, dia: e.target.value })} className={inputCls} data-testid="calc-select-steel-dia">
            {[8, 10, 12, 16, 20, 25, 32].map((d) => (
              <option key={d} value={d}>{d} mm TMT</option>
            ))}
          </select>
        </label>
        <Field label="Number of rods (12 m each)" type="number" min="0" value={f.rods} onChange={(e) => setF({ ...f, rods: e.target.value })} testid="calc-input-steel-rods" />
        <Field label="Rate (₹ / MT)" type="number" min="0" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} testid="calc-input-steel-rate" />
        <p className="font-mono text-[11px] text-gray-600">Unit weight = d²/162 kg/m · Standard rod length 12 m</p>
      </div>
      <div className="bg-ink border border-white/5 p-6">
        <ResultRow label="Unit weight" value={fmt(r.kgm, 3)} unit="kg/m" />
        <ResultRow label="Weight per rod" value={fmt(r.rodWt, 1)} unit="kg" />
        <ResultRow label="Total weight" value={`${inr(r.totalKg)} / ${fmt(r.totalMT, 3)}`} unit="kg / MT" highlight />
        <ResultRow label="Est. steel cost" value={`₹${inr(r.cost)}`} unit="" highlight />
        <ActionBar message={`Steel estimate: ${f.rods} rods of ${f.dia}mm TMT (12m) → ${inr(r.totalKg)} kg (${fmt(r.totalMT, 3)} MT). Please quote landed rate.`} />
      </div>
    </div>
  );
}

function AggregateCalc() {
  const [f, setF] = useState({ qty: "10", from: "brass", material: "aggregate" });
  const r = useMemo(() => {
    const density = f.material === "sand" ? 1.6 : 1.55;
    let m3 = 0;
    if (f.from === "brass") m3 = n(f.qty) * 2.83168;
    else if (f.from === "m3") m3 = n(f.qty);
    else m3 = n(f.qty) / density;
    return { m3, ft3: m3 * 35.3147, brass: m3 / 2.83168, tonnes: m3 * density };
  }, [f]);

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        <Field label="Quantity" type="number" min="0" step="any" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} testid="calc-input-agg-qty" />
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Convert From</span>
            <select value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className={inputCls} data-testid="calc-select-agg-from">
              <option value="brass">Brass (100 ft³)</option>
              <option value="m3">Cubic metre</option>
              <option value="tonnes">Tonnes</option>
            </select>
          </label>
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">Material</span>
            <select value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} className={inputCls} data-testid="calc-select-agg-material">
              <option value="aggregate">Coarse aggregate (1.55 t/m³)</option>
              <option value="sand">Sand (1.60 t/m³)</option>
            </select>
          </label>
        </div>
        <p className="font-mono text-[11px] text-gray-600">1 brass = 100 ft³ = 2.832 m³</p>
      </div>
      <div className="bg-ink border border-white/5 p-6">
        <ResultRow label="Cubic metres" value={fmt(r.m3, 2)} unit="m³" />
        <ResultRow label="Cubic feet" value={fmt(r.ft3, 0)} unit="ft³" />
        <ResultRow label="Brass" value={fmt(r.brass, 2)} unit="brass" />
        <ResultRow label="Tonnes" value={fmt(r.tonnes, 2)} unit="t" highlight />
        <ActionBar message={`${f.material === "sand" ? "Sand" : "Aggregate"} requirement: ${f.qty} ${f.from} ≈ ${fmt(r.tonnes, 2)} tonnes (${fmt(r.brass, 2)} brass). Please quote landed rate.`} />
      </div>
    </div>
  );
}

export default function Calculators() {
  const [tab, setTab] = useState("concrete");

  return (
    <main className="bg-ink min-h-screen pt-16">
      <div className="relative h-56 sm:h-64 overflow-hidden border-b border-white/10">
        <img src={BANNER} alt="Engineer drafting blueprints" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-3 flex items-center gap-3">
              <CalcIcon className="h-4 w-4" /> Contractor Tools Suite
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              Material Estimators
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`relative inline-flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wide px-5 py-3 border transition-colors ${
                tab === t.id
                  ? "border-brand bg-brand text-ink"
                  : "border-white/15 text-gray-400 hover:text-white hover:border-brand/50"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="bg-surface border border-white/10 p-6 sm:p-10"
          >
            {tab === "concrete" && <ConcreteCalc />}
            {tab === "brickwork" && <BrickworkCalc />}
            {tab === "plaster" && <PlasterCalc />}
            {tab === "steel" && <SteelCalc />}
            {tab === "aggregates" && <AggregateCalc />}
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  );
}
